import * as React from 'react'
import { vsprintf } from 'sprintf-js'

import { spacing, theme, styled, Text } from '@thg-commerce/gravity-theme'

import {
  NavigationTree,
  NavigationItem,
  NavigationItemType,
} from '../NavigationTree'

export interface AmpFlyoutMenuProps {
  uniqueGlobalIdentifier: string
  navigationTree: NavigationTree
  i18n: I18n
  uris: {
    home: string
  }
}

interface I18n {
  closeAriaLabel: string
  contactUsText: string
  contactUsLink: string
  helpText: string
  helpLink: string
  homeLabel: string
  subMenuAriaLabel: string
  backText: string
  linkWithChildren: string
  homeTemplate: string
}

const FlyoutHeader = styled.div`
  position: sticky;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  padding: ${spacing(1)};
  z-index: 5;
  background: ${(props) => props.theme.colors.palette.greys.white};
`

// This construction is necessary as styled-components filters out 'non-valid'
// (custom) attributes, such as the ones we need to make AMP work
// Specifically, it filters: amp-nested-submenu-open
export const ProxyButton = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
) => {
  return <button {...props}>{props.children}</button>
}

const FlyoutHeaderButton = styled(ProxyButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.palette.brand.base};
  border: 2px solid transparent;
  height: 40px;
  min-width: 40px;

  svg,
  path {
    fill: ${(props) => props.theme.colors.palette.brand.base};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.palette.brand.base};
  }
`

const StyledUl = styled.ul`
  max-height: calc(100% - 56px);
  display: flex;
  flex-direction: column;
  padding-bottom: ${spacing(1)};
  background: ${(props) => props.theme.colors.palette.greys.white};
`

const ContentUl = styled.ul`
  height: calc(100% - 56px);
  overflow: scroll;
`

const FlyoutMenuText = styled.span`
  ${Text('bodyText', 'default')}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: clip;
`

const FlyoutLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  width: 100%;
  padding: 0 ${spacing(2)};
  border: 2px solid transparent;
  text-decoration: none;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.palette.brand.base};
  }
`

const ImageCardLink = styled.a`
  text-decoration: none;
`

// Added this so that focus border doesn't hug the 'back' text in the flyout
// submenu
const BackTextExtraStyling = styled.span`
  padding: 0 ${spacing(1)} 0 0;
`

const TextCardExtraStyling = styled.div`
  padding: ${spacing(2)} 0;
`

const CardImageTextExtraStyling = styled.div`
  min-height: 40px;
  width: 100%;
  padding: ${spacing(1)} ${spacing(2)};
  border: 2px solid transparent;
`

const FlyoutMenuTextExtraStyling = styled.span`
  padding: 0 ${spacing(1)};
  max-width: 90%;
`

const ThumbnailTextExtraStyling = styled.div`
  margin-left: ${spacing(2)};
`

const StyledFlyoutPanelButton = styled(ProxyButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  width: 100%;
  padding: 0 ${spacing(1)};
  border: 2px solid transparent;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.palette.brand.base};
  }
`

const NestedSubMenu = styled(
  ({
    greyBackground,
    ...props
  }: React.AllHTMLAttributes<HTMLDivElement> & {
    greyBackground?: boolean
  }) => <div {...props}>{props.children}</div>,
)`
  background: ${(props) =>
    props.greyBackground
      ? theme.colors.palette.greys.lighter
      : theme.colors.palette.greys.white};
`

const CardWrapper = styled.div`
  background: ${(props) => props.theme.colors.palette.greys.white};
  margin: ${spacing(2)};
`

const IMAGE_CARD_IMAGE_HEIGHT = 150

const OverflowHiddenContainer = styled.div`
  overflow: hidden;
  height: ${IMAGE_CARD_IMAGE_HEIGHT}px;
`

const ThumbnailContent = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${spacing(2)};
  align-items: center;

  amp-img {
    border-radius: 50%;
  }
`

export const AmpFlyoutMenu = ({
  uniqueGlobalIdentifier,
  navigationTree,
  i18n,
  uris,
}: AmpFlyoutMenuProps) => {
  if (!navigationTree) {
    return null
  }

  return (
    <amp-sidebar
      id={uniqueGlobalIdentifier}
      layout="nodisplay"
      style={{
        width: '300px',
        height: '100vh',
        background: theme.colors.palette.greys.lighter,
      }}
    >
      <amp-nested-menu layout="fill">
        <FlyoutHeader>
          <FlyoutHeaderButton
            on={`tap:AMP.navigateTo(url='${uris.home}')`}
            aria-label={i18n.homeLabel}
            role="button"
            tabIndex={0}
          >
            <amp-img
              width="24px"
              height="24px"
              src="//s1.thcdn.com/enterprise/icons/0.14.1/assets/home.svg"
              alt=""
            />
          </FlyoutHeaderButton>
          <FlyoutHeaderButton
            aria-label={i18n.closeAriaLabel}
            on="tap:flyoutmenu.close"
            role="button"
            tabIndex={0}
          >
            <amp-img
              width="24px"
              height="24px"
              src="//s1.thcdn.com/enterprise/icons/0.14.1/assets/close.svg"
              alt=""
            />
          </FlyoutHeaderButton>
        </FlyoutHeader>
        <StyledUl>
          {navigationTree.navigation.topLevel.map((nav, index) => {
            return <SubNav nav={nav} key={index} i18n={i18n} />
          })}
        </StyledUl>
        <ul aria-label={i18n.subMenuAriaLabel} data-testid="submenu">
          <li>
            <FlyoutLink href={i18n.contactUsLink}>
              <FlyoutMenuText>{i18n.contactUsText}</FlyoutMenuText>
            </FlyoutLink>
          </li>
          <li>
            <FlyoutLink href={i18n.helpLink}>
              <FlyoutMenuText>{i18n.helpText}</FlyoutMenuText>
            </FlyoutLink>
          </li>
        </ul>
      </amp-nested-menu>
    </amp-sidebar>
  )
}

interface SubNavProps {
  nav: NavigationItem
  parentType?: NavigationItemType
  i18n: I18n
}

const SubNav = ({ nav, parentType, i18n }: SubNavProps) => {
  if (nav.subNavigation && nav.subNavigation.length > 0) {
    const isTopLevelImageOrThumbnailOrTextCard =
      nav.type === NavigationItemType.TEXT_CARD ||
      nav.type === NavigationItemType.IMAGE_CARD ||
      nav.type === NavigationItemType.THUMBNAIL_CARD

    const isTopLevelFeatured =
      nav.subNavigation[0].type === NavigationItemType.FEATURED

    return (
      <li>
        <StyledFlyoutPanelButton
          amp-nested-submenu-open=""
          aria-label={vsprintf(i18n.linkWithChildren, [nav.displayName])}
        >
          <FlyoutMenuTextExtraStyling>
            <FlyoutMenuText>{nav.displayName}</FlyoutMenuText>
          </FlyoutMenuTextExtraStyling>
          <amp-img
            width="24px"
            height="24px"
            src="//s1.thcdn.com/enterprise/icons/0.14.1/assets/chevron-right.svg"
            alt=""
          />
        </StyledFlyoutPanelButton>
        <NestedSubMenu
          amp-nested-submenu=""
          greyBackground={isTopLevelImageOrThumbnailOrTextCard}
        >
          <FlyoutHeader>
            <FlyoutHeaderButton
              amp-nested-submenu-close=""
              role="button"
              tabIndex={0}
            >
              <amp-img
                width="24px"
                height="24px"
                src="//s1.thcdn.com/enterprise/icons/0.14.5/assets/chevron-left.svg"
                alt=""
              />
              <FlyoutMenuText>
                <BackTextExtraStyling>{i18n.backText}</BackTextExtraStyling>
              </FlyoutMenuText>{' '}
            </FlyoutHeaderButton>
            <FlyoutHeaderButton
              aria-label={i18n.closeAriaLabel}
              on="tap:flyoutmenu.close"
              role="button"
              tabIndex={0}
            >
              <amp-img
                width="24px"
                height="24px"
                src="//s1.thcdn.com/enterprise/icons/0.14.1/assets/close.svg"
                alt=""
              />
            </FlyoutHeaderButton>
          </FlyoutHeader>
          <ContentUl>
            {isTopLevelImageOrThumbnailOrTextCard && (
              <TextCard nav={nav} key={'home'} addHome={true} i18n={i18n} />
            )}
            {isTopLevelFeatured && (
              <TextLink nav={nav} key={'home'} addHome={true} i18n={i18n} />
            )}
            {nav.subNavigation.map((subNav, index) => {
              return (
                <SubNav
                  parentType={nav.type}
                  nav={subNav}
                  key={index}
                  i18n={i18n}
                />
              )
            })}
          </ContentUl>
        </NestedSubMenu>
      </li>
    )
  }

  switch (parentType) {
    case NavigationItemType.TEXT_CARD:
      return <TextCard nav={nav} i18n={i18n} />
    case NavigationItemType.THUMBNAIL_CARD:
      return (
        <li>
          <CardWrapper>
            <ImageCardLink
              href={nav.link?.url}
              target={nav.link?.openExternally ? '_blank' : '_self'}
              rel={`${nav.link?.noFollow ? 'nofollow' : ''} ${
                nav.link?.noIndex ? 'noindex' : ''
              }`}
            >
              <ThumbnailContent>
                <amp-img
                  src={nav.image?.url}
                  height="56px"
                  width="56px"
                  alt={nav.image?.alt || ''}
                />
                <ThumbnailTextExtraStyling>
                  <FlyoutMenuText>{nav.displayName}</FlyoutMenuText>
                </ThumbnailTextExtraStyling>
              </ThumbnailContent>
            </ImageCardLink>
          </CardWrapper>
        </li>
      )
    case NavigationItemType.IMAGE_CARD:
      return (
        <li>
          <CardWrapper>
            <ImageCardLink
              href={nav.link?.url}
              target={nav.link?.openExternally ? '_blank' : '_self'}
              rel={`${nav.link?.noFollow ? 'nofollow' : ''} ${
                nav.link?.noIndex ? 'noindex' : ''
              }`}
            >
              <OverflowHiddenContainer>
                <amp-img
                  height={`${IMAGE_CARD_IMAGE_HEIGHT}px`}
                  width="268px"
                  alt={nav.image?.alt || ''}
                  src={nav.image?.url}
                />
              </OverflowHiddenContainer>
              <CardImageTextExtraStyling>
                <FlyoutMenuText>{nav.displayName}</FlyoutMenuText>
              </CardImageTextExtraStyling>
            </ImageCardLink>
          </CardWrapper>
        </li>
      )
    default:
      return <TextLink i18n={i18n} nav={nav} />
  }
}

const TextCard = (props: {
  nav: NavigationItem
  i18n: I18n
  addHome?: boolean
}) => {
  const { nav, addHome, i18n } = props

  return (
    <li>
      <CardWrapper>
        <FlyoutLink
          href={nav.link?.url}
          target={nav.link?.openExternally ? '_blank' : '_self'}
          rel={`${nav.link?.noFollow ? 'nofollow' : ''} ${
            nav.link?.noIndex ? 'noindex' : ''
          }`}
        >
          <TextCardExtraStyling>
            <FlyoutMenuText>
              {addHome
                ? vsprintf(i18n.homeTemplate, [nav.displayName])
                : nav.displayName}
            </FlyoutMenuText>
          </TextCardExtraStyling>
        </FlyoutLink>
      </CardWrapper>
    </li>
  )
}

const TextLink = (props: {
  nav: NavigationItem
  i18n: I18n
  addHome?: boolean
}) => {
  const { nav, addHome, i18n } = props
  return (
    <li>
      <FlyoutLink
        href={nav.link?.url}
        target={nav.link?.openExternally ? '_blank' : '_self'}
        rel={`${nav.link?.noFollow ? 'nofollow' : ''} ${
          nav.link?.noIndex ? 'noindex' : ''
        }`}
      >
        <FlyoutMenuText>
          {addHome
            ? vsprintf(i18n.homeTemplate, [nav.displayName])
            : nav.displayName}
        </FlyoutMenuText>
      </FlyoutLink>
    </li>
  )
}
